/**
 * Custom Hook: useStepNavigation
 * 
 * Handles step navigation logic including:
 * - Moving between steps
 * - Handling service type branching
 * - Tracking navigation events
 * - Keyboard navigation
 */

import { useCallback, useEffect, useRef } from 'react';
import { useFunnel } from '../context/FunnelContext';
import { ServiceType } from '../types/funnel.types';
import { 
  trackStepCompleted, 
  trackBackButton,
  calculateTimeSpent 
} from '../utils/analytics';

interface UseStepNavigationOptions {
  // Whether to track analytics events
  enableAnalytics?: boolean;
  // Whether to enable keyboard navigation (Enter to continue)
  enableKeyboardNav?: boolean;
  // Callback when step changes
  onStepChange?: (step: number, direction: 'forward' | 'backward') => void;
}

/**
 * Hook for handling step navigation in the funnel
 */
export function useStepNavigation(options: UseStepNavigationOptions = {}) {
  const {
    enableAnalytics = true,
    enableKeyboardNav = true,
    onStepChange,
  } = options;

  const funnel = useFunnel();
  const stepStartTime = useRef<string>(new Date().toISOString());

  // Track time spent on each step
  useEffect(() => {
    stepStartTime.current = new Date().toISOString();
  }, [funnel.state.currentStep]);

  /**
   * Navigate to the next step
   * Handles service type setting when on the service selection step
   */
  const goNext = useCallback((answer?: unknown) => {
    const currentQuestion = funnel.getQuestionForCurrentStep();
    
    // Track completion if analytics enabled
    if (enableAnalytics && currentQuestion) {
      const timeOnStep = calculateTimeSpent(stepStartTime.current);
      trackStepCompleted(
        funnel.state.currentStep,
        currentQuestion.id,
        currentQuestion.type === 'textarea' || currentQuestion.type === 'text' 
          ? 'text' 
          : currentQuestion.type === 'multi-field' 
            ? 'multi-field' 
            : 'selection',
        timeOnStep
      );
    }

    // If this is the service type question, set the service type
    if (currentQuestion?.id === 'service-type' && answer) {
      funnel.setServiceType(answer as ServiceType);
    }

    // Move to next step
    funnel.nextStep();
    
    // Call callback if provided
    if (onStepChange) {
      onStepChange(funnel.state.currentStep + 1, 'forward');
    }
  }, [funnel, enableAnalytics, onStepChange]);

  /**
   * Navigate to the previous step
   */
  const goBack = useCallback(() => {
    const fromStep = funnel.state.currentStep;
    
    // Track back navigation if analytics enabled
    if (enableAnalytics) {
      trackBackButton(fromStep, fromStep - 1);
    }

    // Move to previous step
    funnel.previousStep();
    
    // Call callback if provided
    if (onStepChange) {
      onStepChange(funnel.state.currentStep - 1, 'backward');
    }
  }, [funnel, enableAnalytics, onStepChange]);

  /**
   * Jump to a specific step
   */
  const goToStep = useCallback((step: number) => {
    const direction = step > funnel.state.currentStep ? 'forward' : 'backward';
    funnel.goToStep(step);
    
    if (onStepChange) {
      onStepChange(step, direction);
    }
  }, [funnel, onStepChange]);

  /**
   * Complete the funnel and go to thank you page
   */
  const completeFunnel = useCallback(() => {
    funnel.completeFunnel();
    funnel.nextStep();
  }, [funnel]);

  /**
   * Enable keyboard navigation (Enter to continue)
   */
  useEffect(() => {
    if (!enableKeyboardNav) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle Enter key
      if (event.key !== 'Enter') return;
      
      // Don't handle if user is typing in a textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;
      
      // Don't handle if target is a button (let native click handle it)
      if (target.tagName === 'BUTTON') return;

      // Don't handle if there's a modifier key
      if (event.shiftKey || event.ctrlKey || event.metaKey) return;

      // This will be handled by form submission or button click
      // Just a placeholder for potential custom handling
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboardNav]);

  return {
    // Navigation functions
    goNext,
    goBack,
    goToStep,
    completeFunnel,
    
    // State helpers
    canGoBack: funnel.state.currentStep > 1,
    canGoForward: !funnel.state.isComplete,
    currentStep: funnel.state.currentStep,
    totalSteps: funnel.getTotalQuestionsForFlow() + 1, // +1 for thank you
  };
}

export default useStepNavigation;
